import {useRouter} from 'next/router';
import {HTMLAttributes, MouseEvent, useCallback} from 'react';
import {requestContext} from '../../../../common/requestContext';
import useTypedSelector from '../../../../common/useTypedSelector';
import {ContentHomeBottomFixedButton} from '../../../../design-system/organisms/ContentHomeBottomFixedButton/ContentHomeBottomFixedButton';
import {ContentHomeTicketActionArea} from '../../../../design-system/organisms/ContentHomeTicketActionArea/ContentHomeTicketActionArea';
import {QueryFromPage} from '../../../../graphql/generated/types';
import logger from '../../../../log/logger';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {useBuyTicketFlow} from '../../../buy/ticket/hook/useBuyTicketFlow';
import {useContentMainTicketActionInfo} from '../hook/useContentMainTicketActionInfo';
import {useContentProductListAnchor} from '../hook/useContentProductListAnchor';
import {useGetShouldShowAnchorButton} from '../hook/useGetShouldShowAnchorButton';
import {ContentHomeTabType} from '../type';
import {getTitleWithParsedEpisode} from '../util/getTitleWithParsedEpisode';

export interface ContentMainTicketActionContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
  tabType: ContentHomeTabType;
}

export function ContentMainTicketActionContainer({
  seriesId,
  className,
  tabType,
  ...props
}: ContentMainTicketActionContainerProps) {
  const {isMobile} = requestContext.requestInfo.display;
  const {ticketActionInfo} = useContentMainTicketActionInfo({
    seriesId,
    isInPreviewTab: tabType === ContentHomeTabType.preview,
  });
  const showAnchorButton = useGetShouldShowAnchorButton({seriesId, tabType});
  const {category, subcategory} =
    useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG).data
      ?.contentHomeOverview?.content || {};

  const {canRead, selectedSortOption} = useTypedSelector(state => ({
    // 판매중이면 읽을 수 있는 회차지만 판매중지여도 읽을 수 있는 회차(소장한 회차나 대여중인 회차)가 있다면 읽을 수 있는 권한이 있으므로 '이어보기'를 보여줌
    canRead: state.contentHome.canRead[seriesId],
    selectedSortOption: state.contentHome.productListSortOption,
  }));
  const {triggerAnchor} = useContentProductListAnchor({
    seriesId,
    selectedSortOption,
  });

  const {startTicketProcess} = useBuyTicketFlow();

  const router = useRouter();
  const handleInViewRelativeButton = useCallback(() => {
    aggregateAndSendLog({
      actionType: 'ViewImp',
      actionName: '연계작품_노출',
      customProps: {
        user_action: 'imp',
      },
      viewimp_contents: {
        imp_id: 'contentlink',
        imp_provider: 'service',
        imp_type: 'vimp',
        layer1: '회차바로보기',
        id: String(ticketActionInfo?.relatedSeries?.seriesId),
        type: 'seriesId',
        name: ticketActionInfo?.relatedSeries?.title,
      },
    });
  }, [ticketActionInfo?.relatedSeries]);
  const handleClickRelatedSeries = useCallback(
    (e: MouseEvent, buttonText: string) => {
      aggregateAndSendLog({
        targetElem: e.target as HTMLElement,
        actionType: 'Event',
        actionName: '연계작품_클릭',
        customProps: {
          user_action: 'click',
        },
        eventMeta: {
          id: String(ticketActionInfo?.relatedSeries?.seriesId),
          type: 'seriesId',
          name: ticketActionInfo?.relatedSeries?.title,
          category: ticketActionInfo?.relatedSeries?.category,
          subcategory: ticketActionInfo?.relatedSeries?.subcategory,
          provider: 'service',
        },
        click: {
          layer1: '회차바로보기',
          copy: buttonText,
          imp_id: 'contentlink',
          imp_provider: 'service',
        },
      });

      router.push(`/content/${ticketActionInfo?.relatedSeries?.seriesId}`);
    },
    [router, ticketActionInfo?.relatedSeries]
  );

  const handleClickAnchor = useCallback(
    (e: MouseEvent) => {
      aggregateAndSendLog({
        targetElem: e.target as HTMLElement,
        actionType: 'Event',
        actionName: '보던회차_클릭',
        customProps: {
          user_action: 'click',
        },
        click: {
          layer1: '회차바로보기',
          copy: '보던회차',
        },
      });

      triggerAnchor();
    },
    [triggerAnchor]
  );

  const handleClickContinue = useCallback(
    (e: MouseEvent) => {
      aggregateAndSendLog({
        targetElem: e.target as HTMLElement,
        actionType: 'Event',
        actionName: '회차_클릭',
        customProps: {
          user_action: 'click',
        },
        eventMeta: {
          id: String(ticketActionInfo?.productId),
          type: 'productId',
          name: ticketActionInfo?.title,
          category,
          subcategory,
          provider: '서비스',
        },
        click: {
          layer1: '회차바로보기',
          copy: ticketActionInfo?.copy,
        },
      });

      if (!ticketActionInfo) {
        logger.err('ticketActionInfo 가 없는 상태에서 클릭');
        return;
      }
      const {productId, pickExactly} = ticketActionInfo;
      if (!productId) {
        logger.err('productId 가 없는 상태에서 클릭');
        return;
      }

      startTicketProcess({
        seriesId,
        targetProductId: productId,
        queryFrom: QueryFromPage.ContentHome,
        nonstopWatching: false,
        pickExactly, // 다음화 보기만 명확하게 선택한 것으로 보고, 그게 아닌 경우 모두 애매한 상황으로 본다
      });
    },
    [category, seriesId, startTicketProcess, subcategory, ticketActionInfo]
  );

  if (!canRead || !ticketActionInfo) {
    return null;
  }

  return isMobile ? (
    <ContentHomeTicketActionArea
      title={ticketActionInfo.buttonTitle}
      subTitle={
        !ticketActionInfo.shouldShowTitle
          ? undefined
          : getTitleWithParsedEpisode(ticketActionInfo.title).title || ''
      }
      episode={getTitleWithParsedEpisode(ticketActionInfo.title).episode || ''}
      hasUnreadSingle={ticketActionInfo.hasUnreadSingle}
      relatedSeries={ticketActionInfo.relatedSeries}
      hasLastReadHistory={ticketActionInfo.hasLastReadHistory}
      showAnchorButton={showAnchorButton}
      onClickGoToRelatedSeries={handleClickRelatedSeries}
      onClickTicketActionButton={e => handleClickContinue(e)}
      onClickAnchor={e => handleClickAnchor(e)}
      onInViewRelativeButton={handleInViewRelativeButton}
      className={className}
      {...props}
    />
  ) : (
    <ContentHomeBottomFixedButton
      title={ticketActionInfo.buttonTitle}
      subTitle={
        !ticketActionInfo.shouldShowTitle
          ? undefined
          : getTitleWithParsedEpisode(ticketActionInfo.title).title || ''
      }
      episode={getTitleWithParsedEpisode(ticketActionInfo.title).episode || ''}
      isMobile={isMobile}
      onClickButton={e => handleClickContinue(e)}
      className={className}
      {...props}
    />
  );
}
